<template>
  <div
    class="bg-white rounded-lg sm:rounded-2xl border-2 border-black shadow-[3px_3px_0_0_rgba(0,0,0,1)] sm:shadow-[6px_4px_0_0_rgba(0,0,0,1)] p-3 sm:p-4 md:p-6 w-full max-w-full"
  >
    <div class="flex items-center min-w-0">
      <div class="flex-shrink-0">
        <div
          class="w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center"
          :class="iconColorClasses[color]"
        >
          <component :is="iconComponent" :class="iconTextClasses[color]" />
        </div>
      </div>
      <div class="ml-3 sm:ml-4 min-w-0 flex-1 overflow-hidden">
        <p class="text-xs sm:text-sm font-medium text-gray-600 truncate">{{ title }}</p>
        <div v-if="isLoading" class="flex items-center space-x-1 mt-1 sm:mt-2">
          <div
            class="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-gray-400 rounded-full animate-bounce"
            style="animation-delay: 0ms"
          ></div>
          <div
            class="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-gray-400 rounded-full animate-bounce"
            style="animation-delay: 150ms"
          ></div>
          <div
            class="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-gray-400 rounded-full animate-bounce"
            style="animation-delay: 300ms"
          ></div>
        </div>
        <p v-else class="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 truncate">
          {{ displayValue }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import {
  CalendarIcon,
  BookOpenIcon,
  StarIcon,
  ClockIcon,
  BoltIcon,
} from "@heroicons/vue/24/outline";

interface Props {
  icon: string;
  title: string;
  value: string | number;
  color: "blue" | "green" | "yellow" | "purple";
  isRating?: boolean;
  isLoading?: boolean;
}

const props = defineProps<Props>();

const iconComponent = computed(() => {
  const icons = {
    calendar: CalendarIcon,
    check: BookOpenIcon,
    star: StarIcon,
    clock: ClockIcon,
    brain: BoltIcon,
  };
  return icons[props.icon as keyof typeof icons] || CalendarIcon;
});

const isLoading = computed(() => {
  return props.isLoading || props.value === "..." || props.value === "...";
});

const displayValue = computed(() => {
  if (props.isRating && props.title.toLowerCase().includes("rating")) {
    // ✅ Check if value is already "N/A" and preserve it
    if (props.value === "N/A") {
      return "N/A";
    }
    const numValue = parseFloat(props.value.toString());
    return isNaN(numValue) ? "N/A" : numValue.toFixed(1);
  }
  return props.value.toString();
});

const iconColorClasses = {
  blue: "bg-blue-200 border border-blue-500",
  green: "bg-green-200 border border-green-500",
  yellow: "bg-yellow-200 border border-yellow-500",
  purple: "bg-purple-200 border border-purple-500",
};

const iconTextClasses = {
  blue: "w-5 h-5 sm:w-6 sm:h-6 text-blue-600",
  green: "w-5 h-5 sm:w-6 sm:h-6 text-green-600",
  yellow: "w-5 h-5 sm:w-6 sm:h-6 text-yellow-600",
  purple: "w-5 h-5 sm:w-6 sm:h-6 text-purple-600",
};
</script>
